import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import DepartmentSelect from '@/components/Form/Shared/DepartmentSelect'
import type { Control, UseFormWatch } from 'react-hook-form'
import type { DataAccessFormValues, ScopeMode } from '../../types'

interface SectionScopeProps {
    control: Control<DataAccessFormValues>
    watch: UseFormWatch<DataAccessFormValues>
}

const SCOPE_OPTIONS: { value: ScopeMode; labelKey: string }[] = [
    {
        value: 'self_plus_teammates',
        labelKey: 'data-access-scope-self-plus-teammates',
    },
    { value: 'departments', labelKey: 'data-access-scope-departments' },
    { value: 'company', labelKey: 'data-access-scope-company' },
]

/**
 * Section 2 — WHAT data can they see? (scope)
 * Radio: self_plus_teammates | departments | company + conditional select
 */
const SectionScope = ({ control, watch }: SectionScopeProps) => {
    const { t } = useTranslation('settings')
    const scopeMode = watch('scope_mode')

    return (
        <div className="bg-white rounded-lg p-6 mb-4">
            <h3 className="text-base font-semibold text-gray-900 mb-1">
                {t('data-access-section-scope-title')}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
                {t('data-access-section-scope-helper')}
            </p>

            {/* Radio group */}
            <Controller
                control={control}
                name="scope_mode"
                render={({ field, fieldState }) => (
                    <div className="space-y-2 mb-4">
                        {SCOPE_OPTIONS.map((opt) => (
                            <label
                                key={opt.value}
                                className="flex items-center gap-2 cursor-pointer"
                            >
                                <input
                                    type="radio"
                                    value={opt.value}
                                    checked={field.value === opt.value}
                                    onChange={() => field.onChange(opt.value)}
                                    className="accent-primary"
                                />
                                <span className="text-sm text-gray-700">
                                    {t(opt.labelKey)}
                                </span>
                            </label>
                        ))}
                        {fieldState.error && (
                            <p className="text-xs text-red-500">
                                {fieldState.error.message}
                            </p>
                        )}
                    </div>
                )}
            />

            {/* Conditional input */}
            {scopeMode === 'self_plus_teammates' && (
                <div className="mt-2 space-y-1">
                    <EmployeeSelect
                        control={control}
                        name="teammates"
                        multiple
                        label="data-access-teammates"
                    />
                    <p className="text-xs text-gray-400">
                        {t('data-access-scope-self-plus-teammates-hint')}
                    </p>
                </div>
            )}
            {scopeMode === 'departments' && (
                <div className="mt-2">
                    <DepartmentSelect
                        control={control}
                        name="scope_departments"
                        label="data-access-departments"
                    />
                </div>
            )}
            {scopeMode === 'company' && (
                <p className="text-sm text-gray-500 italic mt-2">
                    {t('data-access-scope-company-hint')}
                </p>
            )}
        </div>
    )
}

export default SectionScope
